import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputMask } from 'primereact/inputmask';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import procuracaoService from '../services/procuracaoService';
import '../styles/pages/procuracao.css';

export default function Procuracao() {
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useRef(null);
    const [formData, setFormData] = useState({
        tipo: 'PF',
        nome: '',
        cpf_cnpj: '',
        rg: '',
        rep_nome: '',
        rep_cpf: '',
        rep_rg: '',
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cep: '',
        municipio: 'Betim',
        uf: 'MG',
        cidade: 'Betim',
        data: new Date()
    });

    const [loading, setLoading] = useState(false);
    const [previewDialog, setPreviewDialog] = useState(false);
    const [preview, setPreview] = useState({});
    const [ordemSourceId, setOrdemSourceId] = useState(null);
    const [cepSearchResults, setCepSearchResults] = useState([]);
    const [showCepDialog, setShowCepDialog] = useState(false);
    const [loadingCepSearch, setLoadingCepSearch] = useState(false);

    // --- Pré-preenchimento da Ordem de Serviço ---
    useEffect(() => {
        if (location.state) {
            setFormData(prev => ({
                ...prev,
                ...location.state
            }));
            if (location.state.ordemId) {
                setOrdemSourceId(location.state.ordemId);
            }
        }
    }, [location.state]);

    const voltar = () => {
        if (ordemSourceId) {
            navigate(`/ordem/${ordemSourceId}`);
        } else {
            navigate('/ordens');
        }
    };

    // --- Funções de Validação (replicadas do Python) ---
    const soDigitos = (s) => (s || '').replace(/\D/g, '');

    const validaCPF = (s) => {
        const n = soDigitos(s);
        if (n.length !== 11 || n === n[0].repeat(11)) return false;
        
        for (let k = 9; k <= 10; k++) {
            let soma = 0;
            for (let i = 0; i < k; i++) {
                soma += parseInt(n[i]) * (k + 1 - i);
            }
            if ((soma * 10) % 11 % 10 !== parseInt(n[k])) return false;
        }
        return true;
    };

    const validaCNPJ = (s) => {
        const n = soDigitos(s);
        if (n.length !== 14 || n === n[0].repeat(14)) return false;
        
        const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        const pesos2 = [6, ...pesos1];
        
        for (let idx = 0; idx < 2; idx++) {
            const pesos = idx === 0 ? pesos1 : pesos2;
            const k = idx === 0 ? 12 : 13;
            let soma = 0;
            for (let i = 0; i < pesos.length; i++) {
                soma += parseInt(n[i]) * pesos[i];
            }
            const dv = 11 - (soma % 11);
            if ((dv > 9 ? 0 : dv) !== parseInt(n[k])) return false;
        }
        return true;
    };

    const validarData = (dataStr) => {
        if (!dataStr) return true;
        const regex = /^\d{4}-\d{2}-\d{2}$/;
        if (!regex.test(dataStr)) return false;
        
        try {
            const [ano, mes, dia] = dataStr.split('-').map(Number);
            new Date(ano, mes - 1, dia);
            return !isNaN(new Date(ano, mes - 1, dia).getTime());
        } catch {
            return false;
        }
    };

    const formatarCPF = (s) => {
        const n = soDigitos(s);
        return n.length === 11 ? `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6, 9)}-${n.slice(9)}` : s;
    };

    const formatarCNPJ = (s) => {
        const n = soDigitos(s);
        return n.length === 14 
            ? `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5, 8)}/${n.slice(8, 12)}-${n.slice(12)}`
            : s;
    };

    const formatarCEP = (s) => {
        const n = soDigitos(s);
        return n.length === 8 ? `${n.slice(0, 5)}-${n.slice(5)}` : s;
    };

    const inferirTipo = (cpfCnpj) => {
        const ndig = soDigitos(cpfCnpj).length;
        return ndig === 11 ? 'PF' : ndig === 14 ? 'PJ' : '';
    };

    // --- Validação Geral ---
    const validar = () => {
        const erros = [];
        const pj = formData.tipo === 'PJ';

        if (!formData.tipo || !['PF', 'PJ'].includes(formData.tipo)) {
            erros.push('Tipo deve ser "PF" ou "PJ"');
        }

        if (!formData.nome?.trim()) {
            erros.push('Campo obrigatório ausente: nome');
        }

        if (!formData.cpf_cnpj?.trim()) {
            erros.push('Campo obrigatório ausente: CPF/CNPJ');
        } else {
            if (pj) {
                if (!validaCNPJ(formData.cpf_cnpj)) {
                    erros.push('CNPJ inválido (dígitos verificadores)');
                }
            } else {
                if (!validaCPF(formData.cpf_cnpj)) {
                    erros.push('CPF inválido (dígitos verificadores)');
                }
            }
        }

        if (!formData.municipio?.trim()) {
            erros.push('Campo obrigatório ausente: município');
        }

        if (!formData.uf?.trim()) {
            erros.push('Campo obrigatório ausente: UF');
        } else if (!/^[A-Z]{2}$/.test(formData.uf.toUpperCase())) {
            erros.push('UF deve ser uma sigla de 2 letras');
        }

        if (pj) {
            if (!formData.rep_nome?.trim()) {
                erros.push('Campo obrigatório ausente (PJ): rep_nome');
            }
            if (!formData.rep_cpf?.trim()) {
                erros.push('Campo obrigatório ausente (PJ): rep_cpf');
            } else if (!validaCPF(formData.rep_cpf)) {
                erros.push('CPF do representante inválido (dígitos verificadores)');
            }
        }

        if (formData.data) {
            const dataISO = formData.data instanceof Date 
                ? formData.data.toISOString().split('T')[0]
                : formData.data;
            if (!validarData(dataISO)) {
                erros.push('Data inválida (use formato AAAA-MM-DD e data existente)');
            }
        }

        return erros;
    };

    // --- Busca de CEP ---
    const buscarCEP = async (cepValue) => {
        // Remove caracteres especiais
        const cepLimpo = soDigitos(cepValue);
        
        // Só busca se tiver 8 dígitos
        if (cepLimpo.length !== 8) {
            return;
        }

        setLoadingCepSearch(true);
        try {
            const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
            const data = await response.json();

            if (data.erro) {
                toast.current.show({
                    severity: 'warn',
                    summary: 'CEP não encontrado',
                    detail: `Nenhum endereço encontrado para o CEP ${formatarCEP(cepLimpo)}`
                });
                return;
            }

            // Formatar resultado para exibição
            const resultado = {
                cep: formatarCEP(data.cep),
                logradouro: data.logradouro,
                bairro: data.bairro,
                municipio: data.localidade,
                uf: data.uf,
                complemento: data.complemento || '',
                original: data
            };

            setCepSearchResults([resultado]);
            setShowCepDialog(true);
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Erro na busca',
                detail: 'Não foi possível buscar o CEP. Tente novamente.'
            });
        } finally {
            setLoadingCepSearch(false);
        }
    };

    // --- Preencher dados do CEP selecionado ---
    const preencherDadosCEP = (resultado) => {
        setFormData(prev => ({
            ...prev,
            cep: resultado.cep,
            logradouro: resultado.logradouro,
            bairro: resultado.bairro,
            municipio: resultado.municipio,
            uf: resultado.uf,
            complemento: resultado.complemento
        }));
        setShowCepDialog(false);
        toast.current.show({
            severity: 'success',
            summary: 'Endereço carregado',
            detail: `Dados do CEP ${resultado.cep} preenchidos com sucesso`
        });
    };

    // --- Manipulação do Formulário ---
    const handleInputChange = (field, value) => {
        let novoValor = value;

        // Autocorrect de tipo se mudar CPF/CNPJ
        if (field === 'cpf_cnpj' && !formData.tipo) {
            const tipo = inferirTipo(value);
            if (tipo) setFormData({ ...formData, tipo });
        }

        // Maiúsculas para UF
        if (field === 'uf') {
            novoValor = value.toUpperCase();
        }

        setFormData({ ...formData, [field]: novoValor });

        // Buscar CEP quando completo (8 dígitos)
        if (field === 'cep' && soDigitos(value).length === 8) {
            buscarCEP(value);
        }
    };

    const gerarPreview = () => {
        const erros = validar();
        if (erros.length > 0) {
            toast.current.show({
                severity: 'error',
                summary: 'Erro de Validação',
                detail: erros.join('; ')
            });
            return;
        }

        // Montar preview do documento
        const pj = formData.tipo === 'PJ';
        const cpfFormatado = pj ? formatarCNPJ(formData.cpf_cnpj) : formatarCPF(formData.cpf_cnpj);
        
        let numero = formData.numero || '';
        if (formData.complemento) {
            numero = `${numero} ${formData.complemento}`.trim();
        }
        const partes = [formData.logradouro, numero, formData.bairro].filter(p => p);
        partes.push(`${formData.municipio} - ${formData.uf}`);
        if (formData.cep) {
            partes.push(formatarCEP(formData.cep));
        }
        const endereco = partes.join(', ');
        const preposicao = formData.logradouro ? 'na' : 'em';

        let outorgante, assinatura;
        if (pj) {
            const ident = formData.rep_rg ? `IDENTIDADE ${formData.rep_rg}, ` : '';
            outorgante = `OUTORGANTE: ${formData.rep_nome}, brasileiro(a), CPF ${formatarCPF(formData.rep_cpf)}, ${ident}representando a empresa ${formData.nome}, regida pelo CNPJ ${cpfFormatado} localizada ${preposicao} ${endereco}`;
            assinatura = [
                formData.rep_nome,
                `CPF ${formatarCPF(formData.rep_cpf)}`,
                `CNPJ ${cpfFormatado}`
            ];
        } else {
            const ident = formData.rg ? `IDENTIDADE ${formData.rg}, ` : '';
            outorgante = `OUTORGANTE: ${formData.nome}, brasileiro(a), CPF ${cpfFormatado}, ${ident}residido(a) ${preposicao} ${endereco}`;
            assinatura = [
                formData.nome,
                formData.rg ? `CPF ${cpfFormatado}, ID ${formData.rg}` : `CPF ${cpfFormatado}`
            ];
        }

        const data = formData.data instanceof Date ? formData.data : new Date(formData.data);
        const dataStr = data.toLocaleDateString('pt-BR');

        setPreview({
            outorgante,
            assinatura,
            data: dataStr,
            cidade: (formData.cidade || 'Betim').toUpperCase()
        });
        setPreviewDialog(true);
    };

    const gerarPDF = async () => {
        const erros = validar();
        if (erros.length > 0) {
            toast.current.show({
                severity: 'error',
                summary: 'Erro de Validação',
                detail: erros.join('; ')
            });
            return;
        }

        setLoading(true);
        try {
            // Preparar dados no mesmo formato do Python
            const dados = {
                tipo: formData.tipo,
                nome: formData.nome,
                cpf_cnpj: formData.cpf_cnpj,
                rg: formData.rg || undefined,
                rep_nome: formData.rep_nome || undefined,
                rep_cpf: formData.rep_cpf || undefined,
                rep_rg: formData.rep_rg || undefined,
                logradouro: formData.logradouro || undefined,
                numero: formData.numero || undefined,
                complemento: formData.complemento || undefined,
                bairro: formData.bairro || undefined,
                cep: formData.cep || undefined,
                municipio: formData.municipio,
                uf: formData.uf,
                cidade: formData.cidade || 'Betim',
                data: formData.data instanceof Date 
                    ? formData.data.toISOString().split('T')[0]
                    : formData.data
            };

            // Chamar serviço para gerar PDF
            const response = await procuracaoService.gerarPDF(dados);

            // Download do PDF
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `procuracao_${formData.nome.replace(/\s+/g, '_')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentElement.removeChild(link);

            toast.current.show({
                severity: 'success',
                summary: 'Sucesso',
                detail: 'Procuração gerada com sucesso'
            });

            setPreviewDialog(false);
        } catch (error) {
            const mensagemErro = error.response?.data?.message || error.message || 'Erro ao gerar procuração';
            toast.current.show({
                severity: 'error',
                summary: 'Erro',
                detail: mensagemErro
            });
        } finally {
            setLoading(false);
        }
    };

    const estadosBrasileiros = [
        { label: 'Acre', value: 'AC' },
        { label: 'Alagoas', value: 'AL' },
        { label: 'Amapá', value: 'AP' },
        { label: 'Amazonas', value: 'AM' },
        { label: 'Bahia', value: 'BA' },
        { label: 'Ceará', value: 'CE' },
        { label: 'Distrito Federal', value: 'DF' },
        { label: 'Espírito Santo', value: 'ES' },
        { label: 'Goiás', value: 'GO' },
        { label: 'Maranhão', value: 'MA' },
        { label: 'Mato Grosso', value: 'MT' },
        { label: 'Mato Grosso do Sul', value: 'MS' },
        { label: 'Minas Gerais', value: 'MG' },
        { label: 'Pará', value: 'PA' },
        { label: 'Paraíba', value: 'PB' },
        { label: 'Paraná', value: 'PR' },
        { label: 'Pernambuco', value: 'PE' },
        { label: 'Piauí', value: 'PI' },
        { label: 'Rio de Janeiro', value: 'RJ' },
        { label: 'Rio Grande do Norte', value: 'RN' },
        { label: 'Rio Grande do Sul', value: 'RS' },
        { label: 'Rondônia', value: 'RO' },
        { label: 'Roraima', value: 'RR' },
        { label: 'Santa Catarina', value: 'SC' },
        { label: 'São Paulo', value: 'SP' },
        { label: 'Sergipe', value: 'SE' },
        { label: 'Tocantins', value: 'TO' }
    ];

    return (
        <div className="page-container">
            <Toast ref={toast} />

            <Card className="page-card">
                <div className="page-header">
                    <h1>Gerador de Procuração</h1>
                    <p>Gere procurações no modelo CEMIG WG SOL</p>
                </div>

                {ordemSourceId && (
                    <div style={{
                        backgroundColor: '#e3f2fd',
                        border: '1px solid #2196F3',
                        borderRadius: '4px',
                        padding: '10px',
                        marginBottom: '10px'
                    }}>
                        <p style={{ margin: 0 }}>
                            ℹ️ Procuração vinculada à Ordem de Serviço #{ordemSourceId}
                        </p>
                    </div>
                )}

                <Card className="page-card" style={{ marginTop: '10px' }}>
                    <div className="page-sub-header">
                        <h2>Dados do Outorgante</h2>
                    </div>

                    {/* Tipo */}
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>Tipo *</label>
                        <Dropdown
                            value={formData.tipo}
                            options={[
                                { label: 'Pessoa Física (PF)', value: 'PF' },
                                { label: 'Pessoa Jurídica (PJ)', value: 'PJ' }
                            ]}
                            onChange={(e) => handleInputChange('tipo', e.value)}
                            style={{ width: '100%' }}
                        />
                    </div>

                    {/* Nome/Razão Social */}
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            {formData.tipo === 'PF' ? 'Nome Completo' : 'Razão Social'} *
                        </label>
                        <InputText
                            value={formData.nome}
                            onChange={(e) => handleInputChange('nome', e.target.value)}
                            placeholder={formData.tipo === 'PF' ? 'João da Silva' : 'Empresa Ltda'}
                            style={{ width: '100%' }}
                        />
                    </div>

                    {/* CPF/CNPJ */}
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            {formData.tipo === 'PF' ? 'CPF' : 'CNPJ'} *
                        </label>
                        <InputMask
                            value={formData.cpf_cnpj}
                            onChange={(e) => handleInputChange('cpf_cnpj', e.value)}
                            mask={formData.tipo === 'PF' ? '999.999.999-99' : '99.999.999/9999-99'}
                            placeholder={formData.tipo === 'PF' ? '000.000.000-00' : '00.000.000/0000-00'}
                            style={{ width: '100%' }}
                        />
                    </div>

                    {/* RG (PF) ou Rep. Legal (PJ) */}
                    {formData.tipo === 'PF' ? (
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px' }}>RG (opcional)</label>
                            <InputText
                                value={formData.rg}
                                onChange={(e) => handleInputChange('rg', e.target.value)}
                                placeholder="MG-12.345.678 SSP MG"
                                style={{ width: '100%' }}
                            />
                        </div>
                    ) : (
                        <>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px' }}>
                                    Nome do Representante Legal *
                                </label>
                                <InputText
                                    value={formData.rep_nome}
                                    onChange={(e) => handleInputChange('rep_nome', e.target.value)}
                                    placeholder="Carlos Silva"
                                    style={{ width: '100%' }}
                                />
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px' }}>
                                    CPF do Representante *
                                </label>
                                <InputMask
                                    value={formData.rep_cpf}
                                    onChange={(e) => handleInputChange('rep_cpf', e.value)}
                                    mask="999.999.999-99"
                                    placeholder="000.000.000-00"
                                    style={{ width: '100%' }}
                                />
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px' }}>
                                    RG do Representante (opcional)
                                </label>
                                <InputText
                                    value={formData.rep_rg}
                                    onChange={(e) => handleInputChange('rep_rg', e.target.value)}
                                    placeholder="MG-12.345.678 SSP MG"
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </>
                    )}
                </Card>

                <Card className="page-card" style={{ marginTop: '10px' }}>
                    <div className="page-sub-header">
                        <h2>Dados de Endereço</h2>
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>CEP (opcional)</label>
                        <InputMask
                            value={formData.cep}
                            onChange={(e) => handleInputChange('cep', e.value)}
                            mask="99999-999"
                            placeholder="00000-000"
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>Logradouro (opcional)</label>
                        <InputText
                            value={formData.logradouro}
                            onChange={(e) => handleInputChange('logradouro', e.target.value)}
                            placeholder="Rua das Flores"
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Número (opcional)</label>
                            <InputText
                                value={formData.numero}
                                onChange={(e) => handleInputChange('numero', e.target.value)}
                                placeholder="123"
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Complemento (opcional)</label>
                            <InputText
                                value={formData.complemento}
                                onChange={(e) => handleInputChange('complemento', e.target.value)}
                                placeholder="Apto 42"
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>Bairro (opcional)</label>
                        <InputText
                            value={formData.bairro}
                            onChange={(e) => handleInputChange('bairro', e.target.value)}
                            placeholder="Centro"
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px', marginBottom: '15px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Município *</label>
                            <InputText
                                value={formData.municipio}
                                onChange={(e) => handleInputChange('municipio', e.target.value)}
                                placeholder="Betim"
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>UF *</label>
                            <Dropdown
                                value={formData.uf}
                                options={estadosBrasileiros}
                                onChange={(e) => handleInputChange('uf', e.value)}
                                style={{ width: '100%' }}
                                filter
                            />
                        </div>
                    </div>

                </Card>

                <Card className="page-card" style={{ marginTop: '10px' }}>
                    <div className="page-sub-header">
                        <h2>Dados da Procuração</h2>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>
                                Cidade para Assinatura (padrão: Betim)
                            </label>
                            <InputText
                                value={formData.cidade}
                                onChange={(e) => handleInputChange('cidade', e.target.value)}
                                placeholder="Betim"
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Data (padrão: hoje)</label>
                            <Calendar
                                value={formData.data}
                                onChange={(e) => handleInputChange('data', e.value)}
                                dateFormat="dd/mm/yy"
                                showIcon
                                placeholder="Selecione a data"
                                style={{ width: '100%' }}
                                locale="pt-BR"
                            />
                        </div>
                    </div>
                </Card>

                <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <Button
                        label="Voltar"
                        icon="pi pi-arrow-left"
                        className="button-cancel"
                        onClick={voltar}
                    />
                    <Button
                        label="Visualizar"
                        icon="pi pi-eye"
                        className="button-confirm"
                        onClick={gerarPreview}
                        loading={loading}
                    />
                    <Button
                        label="Gerar PDF"
                        icon="pi pi-download"
                        className="button-save"
                        onClick={gerarPDF}
                        loading={loading}
                    />
                </div>
            </Card>

            {/* Preview Dialog */}
            <Dialog
                visible={previewDialog}
                style={{ width: '90vw', maxWidth: '900px' }}
                breakpoints={{ '960px': '75vw', '640px': '90vw' }}
                header="Visualizar Procuração"
                modal
                onHide={() => setPreviewDialog(false)}
                footer={
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <Button
                            label="Cancelar"
                            icon="pi pi-times"
                            className="button-cancel"
                            onClick={() => setPreviewDialog(false)}
                        />
                        <Button
                            label="Gerar PDF"
                            icon="pi pi-download"
                            className="button-save"
                            onClick={gerarPDF}
                            loading={loading}
                        />
                    </div>
                }
            >
                <div className="preview-container">
                    <div style={{ marginBottom: '20px', lineHeight: '1.6' }}>
                        <h3 style={{ textAlign: 'center', textDecoration: 'underline', fontWeight: 'bold' }}>
                            INSTRUMENTO PARTICULAR DE PROCURAÇÃO
                        </h3>
                        <p style={{ textAlign: 'justify', marginTop: '20px' }}>
                            {preview.outorgante}
                        </p>
                        <p style={{ textAlign: 'justify' }}>
                            <strong>OUTORGADO:</strong> Rodrigo Dutra Schuchmann, brasileiro, Engenheiro Eletricista, 
                            casado, CPF 013.605.966-07, CREA: MG0000208488D, residente na Av. das Américas, 
                            872 - Centro, Betim - MG, 32600-170.
                        </p>
                        <p style={{ textAlign: 'justify', marginTop: '15px' }}>
                            Pelo presente instrumento particular de procuração, o <strong>OUTORGANTE</strong> supra, 
                            nomeia e constitui seu bastante procurador o <strong>OUTORGADO</strong> acima, ao qual 
                            concede os poderes para fins específicos de representá-lo(s) perante a 
                            <strong>CEMIG DISTRIBUIÇÃO S.A</strong>, sociedade por ações, constituída como subsidiária 
                            integral da Companhia Energética de Minas Gerais, inscrita no CNPJ/MF sob o nº 
                            06.981.180/0001-16, com sede em Belo Horizonte – MG, na Av. Barbacena, 1200, 
                            17º andar – A1, Bairro Santo Agostinho, doravante denominada <strong>CEMIG D</strong>, para 
                            fins de efetuar <strong>a troca de titularidade da instalação,</strong> bem como promover a 
                            execução de obra em rede de distribuição para o <strong>OUTORGANTE</strong>, sob projeto 
                            CEMIG, podendo o <strong>OUTORGADO</strong> efetuar a assinatura da ART- Anotação de 
                            Responsabilidade Técnica da referida obra, representando o <strong>OUTORGANTE.</strong>
                        </p>
                        <p style={{ textAlign: 'center', marginTop: '30px', fontWeight: 'bold' }}>
                            {preview.cidade}, {preview.data}
                        </p>
                        <div style={{ textAlign: 'center', marginTop: '60px', borderTop: '1px solid #000', paddingTop: '20px' }}>
                            {preview.assinatura?.map((linha, idx) => (
                                <p key={idx} style={{ margin: '5px 0' }}>
                                    {linha}
                                </p>
                            ))}
                        </div>
                    </div>
                </div>
            </Dialog>

            {/* CEP Search Dialog */}
            <Dialog
                visible={showCepDialog}
                style={{ width: '90vw', maxWidth: '600px' }}
                breakpoints={{ '960px': '75vw', '640px': '90vw' }}
                header="Endereço Encontrado"
                modal
                onHide={() => setShowCepDialog(false)}
                footer={
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <Button
                            label="Cancelar"
                            icon="pi pi-times"
                            className="button-cancel"
                            onClick={() => setShowCepDialog(false)}
                        />
                        <Button
                            label="Usar Este Endereço"
                            icon="pi pi-check"
                            className="button-save"
                            onClick={() => preencherDadosCEP(cepSearchResults[0])}
                            loading={loadingCepSearch}
                        />
                    </div>
                }
            >
                <div style={{ padding: '20px' }}>
                    {cepSearchResults.length > 0 && (
                        <div>
                            <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                <p style={{ margin: '5px 0' }}>
                                    <strong>CEP:</strong> {cepSearchResults[0].cep}
                                </p>
                                <p style={{ margin: '5px 0' }}>
                                    <strong>Logradouro:</strong> {cepSearchResults[0].logradouro}
                                </p>
                                <p style={{ margin: '5px 0' }}>
                                    <strong>Bairro:</strong> {cepSearchResults[0].bairro}
                                </p>
                                <p style={{ margin: '5px 0' }}>
                                    <strong>Cidade:</strong> {cepSearchResults[0].municipio}
                                </p>
                                <p style={{ margin: '5px 0' }}>
                                    <strong>Estado:</strong> {cepSearchResults[0].uf}
                                </p>
                            </div>
                            <p style={{ color: '#666', fontSize: '14px', marginTop: '15px' }}>
                                Deseja usar este endereço? Será aplicado ao formulário.
                            </p>
                        </div>
                    )}
                </div>
            </Dialog>
        </div>
    );
}

